import { Currency, currencyEquals, WETC } from '@etcswapv2/sdk-legacy'
import { useMemo } from 'react'
import { useWalletClient } from 'wagmi'
import { type Address } from 'viem'
import { wetcAbi } from '../constants/abis'
import { tryParseAmount } from '../state/swap/hooks'
import { useTransactionAdder } from '../state/transactions/hooks'
import { useCurrencyBalance } from '../state/wallet/hooks'
import { useActiveWeb3React } from './index'
import { V2_CONTRACTS, getChainById } from '../wagmi'

export enum WrapType {
  NOT_APPLICABLE,
  WRAP,
  UNWRAP
}

const NOT_APPLICABLE = { wrapType: WrapType.NOT_APPLICABLE }

/**
 * Given the selected input and output currency, return a wrap callback
 * @param inputCurrency the selected input currency
 * @param outputCurrency the selected output currency
 * @param typedValue the user input value
 */
export default function useWrapCallback(
  inputCurrency: Currency | undefined,
  outputCurrency: Currency | undefined,
  typedValue: string | undefined
): { wrapType: WrapType; execute?: undefined | (() => Promise<void>); inputError?: string } {
  const { chainId, account } = useActiveWeb3React()
  const { data: walletClient } = useWalletClient()
  const balance = useCurrencyBalance(account ?? undefined, inputCurrency)
  // we can always parse the amount typed as the input currency, since wrapping is 1:1
  const inputAmount = useMemo(() => tryParseAmount(typedValue, inputCurrency), [inputCurrency, typedValue])
  const addTransaction = useTransactionAdder()

  return useMemo(() => {
    if (!walletClient || !chainId || !inputCurrency || !outputCurrency) return NOT_APPLICABLE

    const wetcAddress = chainId ? V2_CONTRACTS[chainId as 61 | 63]?.WETC : undefined
    if (!wetcAddress) return NOT_APPLICABLE

    const chain = getChainById(chainId)
    if (!chain) return NOT_APPLICABLE

    const wetcToken = WETC[chainId]
    if (!wetcToken) return NOT_APPLICABLE

    const sufficientBalance = inputAmount && balance && !balance.lessThan(inputAmount)

    // Determine input error - prioritize "Enter an amount" when no amount is entered
    const getInputError = (symbol: string): string | undefined => {
      if (!inputAmount) return 'Enter an amount'
      if (!sufficientBalance) return `Insufficient ${symbol} balance`
      return undefined
    }

    // Use property check instead of reference equality to avoid module duplication issues
    const inputIsNative = inputCurrency && !('address' in inputCurrency)
    const outputIsNative = outputCurrency && !('address' in outputCurrency)

    if (inputIsNative && currencyEquals(wetcToken as unknown as Currency, outputCurrency)) {
      return {
        wrapType: WrapType.WRAP,
        execute:
          sufficientBalance && inputAmount
            ? async () => {
                try {
                  // Convert JSBI to bigint for viem
                  const valueAsBigInt = BigInt(inputAmount.raw.toString())

                  const hash = await walletClient.writeContract({
                    address: wetcAddress as Address,
                    abi: wetcAbi,
                    functionName: 'deposit',
                    value: valueAsBigInt,
                    chain,
                    account: account as Address
                  })

                  addTransaction({ hash }, { summary: `Wrap ${inputAmount.toSignificant(6)} ETC to WETC` })
                } catch (error) {
                  console.error('Could not deposit', error)
                }
              }
            : undefined,
        inputError: getInputError('ETC')
      }
    } else if (currencyEquals(wetcToken as unknown as Currency, inputCurrency) && outputIsNative) {
      return {
        wrapType: WrapType.UNWRAP,
        execute:
          sufficientBalance && inputAmount
            ? async () => {
                try {
                  // Convert JSBI to bigint for viem
                  const amountAsBigInt = BigInt(inputAmount.raw.toString())

                  const hash = await walletClient.writeContract({
                    address: wetcAddress as Address,
                    abi: wetcAbi,
                    functionName: 'withdraw',
                    args: [amountAsBigInt],
                    chain,
                    account: account as Address
                  })

                  addTransaction({ hash }, { summary: `Unwrap ${inputAmount.toSignificant(6)} WETC to ETC` })
                } catch (error) {
                  console.error('Could not withdraw', error)
                }
              }
            : undefined,
        inputError: getInputError('WETC')
      }
    } else {
      return NOT_APPLICABLE
    }
  }, [walletClient, chainId, inputCurrency, outputCurrency, inputAmount, balance, addTransaction, account])
}
